import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Download, Share, X, Plus } from "lucide-react";
import { ToothLogo } from "@/components/ToothLogo";

const DISMISS_KEY = "op_install_dismissed";
const DISMISS_DAYS = 7;

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [mode, setMode] = useState(null); // "android" | "ios" | null

  useEffect(() => {
    if (isStandalone()) return;
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_DAYS * 864e5) return;

    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      setMode("android");
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    let iosTimer;
    if (isIOS()) {
      iosTimer = setTimeout(() => setMode((m) => m || "ios"), 2500);
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setMode(null);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setMode(null);
  };

  return (
    <AnimatePresence>
      {mode && (
        <motion.div
          data-testid="install-prompt"
          initial={{ y: -80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -80, opacity: 0 }}
          transition={{ type: "spring", damping: 26, stiffness: 320 }}
          className="fixed inset-x-3 top-3 z-[70] mx-auto max-w-[456px]"
        >
          <div className="glass flex items-center gap-3 rounded-3xl p-3 shadow-[0_16px_40px_-16px_rgba(0,0,0,0.5)]">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-neutral-900">
              <img src="/icon-192.png?v=7" alt="Odontopick" className="h-8 w-8 rounded-lg object-cover" />
            </div>

            {mode === "android" ? (
              <>
                <div className="min-w-0 flex-1">
                  <p className="font-brand text-sm font-semibold text-neutral-900">Instala Odontopick</p>
                  <p className="truncate text-xs text-neutral-600">Acceso directo, más rápido y sin conexión.</p>
                </div>
                <button
                  data-testid="install-btn"
                  onClick={install}
                  className="flex shrink-0 items-center gap-1.5 rounded-full bg-neutral-900 px-4 py-2.5 text-sm font-semibold text-white active:scale-95"
                >
                  <Download className="h-4 w-4" /> Instalar
                </button>
              </>
            ) : (
              <div className="min-w-0 flex-1">
                <p className="font-brand text-sm font-semibold text-neutral-900">Instala Odontopick</p>
                <p className="text-xs leading-snug text-neutral-600">
                  Toca <Share className="inline h-3.5 w-3.5 -translate-y-0.5" /> Compartir y luego{" "}
                  <span className="whitespace-nowrap font-medium text-neutral-800">
                    <Plus className="inline h-3.5 w-3.5 -translate-y-0.5" /> Añadir a pantalla de inicio
                  </span>
                  .
                </p>
              </div>
            )}

            <button
              data-testid="install-dismiss-btn"
              onClick={dismiss}
              aria-label="Cerrar"
              className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-neutral-500 hover:bg-white/50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
